'use client';
import type { FC } from 'react';
import Image from 'next/image';
import { useParams } from 'next/navigation';
import { Box, Flex, Heading, Skeleton, Text } from '@eqi-investimentos/react';
import { Author } from '@/modules/Author/interface/Author';
import { useGetAuthorPrograms } from '@/modules/Author/http/client/useGetAuthorPrograms';
import { IfRender } from '@/utils/jsx';
import { AuthorRecommendations } from '../../Author/ui/Recommendations';
import { AuthorPrograms } from './Programs';

export const AuthorHub: FC<{ author: Author }> = ({ author }) => {
  const authorSlug = useParams<{ slug: string }>().slug;

  const { data: programs = [], isFetching: isFetchingPrograms } =
    useGetAuthorPrograms({ id: authorSlug });

  return (
    <>
      <Flex
        alignItems="center"
        gap="1.875rem"
        p="l"
        w="full"
        bgColor="background.brandLight"
        borderRadius="xs"
        sx={{
          '>div': { _first: { minW: 20, minH: 20 } },
          '.thumb_author': {
            w: '5.75rem',
            h: '5.75rem',
          },
        }}
      >
        <IfRender condition={isFetchingPrograms}>
          <Skeleton w="5rem" h="5rem" borderRadius="0.3125rem" />
        </IfRender>
        <IfRender condition={!isFetchingPrograms}>
          <Image
            className="thumb_author"
            src={author.thumb}
            alt={author.name}
            width={92}
            height={92}
            style={{ borderRadius: '0.3125rem', objectFit: 'cover' }}
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src = '/assets/eqi-placeholder.png';
            }}
          />
        </IfRender>
        <Flex flexDir="column" gap="2">
          <IfRender condition={isFetchingPrograms}>
            <Skeleton
              w="13.75rem"
              h="20px"
              borderRadius="0.125rem"
              mb="0.3125rem"
            />
            <Skeleton
              w="24.375rem"
              h="18px"
              borderRadius="0.125rem"
              mb="0.9375rem"
            />
            <Skeleton w="28.125rem" h="16px" borderRadius="0.125rem" />
          </IfRender>
          <IfRender condition={!isFetchingPrograms}>
            <Heading
              as="h6"
              fontSize="1.25rem"
              fontWeight="normal"
              lineHeight="h6"
              color="content.primary"
            >
              {author.name}
            </Heading>
            <Text
              fontSize={{ base: '0.8125rem', md: 'md' }}
              lineHeight="7"
              color="content.brand"
            >
              {author.summary}
            </Text>
            <Box maxW="800px">
              <Text
                mt="2.5"
                fontSize={{ base: 'xs', md: 'md' }}
                fontWeight="normal"
                lineHeight="4"
                color="content.secondary"
              >
                {author.description_striped}
              </Text>
            </Box>
          </IfRender>
        </Flex>
      </Flex>
      <AuthorRecommendations
        authorName={author.name}
        recommendations={author.portfolios}
        isLoading={isFetchingPrograms}
      />
      <AuthorPrograms
        authorName={author.name}
        isLoading={isFetchingPrograms}
        programs={programs}
      />
    </>
  );
};
